import { useEffect, useState } from 'react';

import { Checkbox, Drawer, InputAdornment } from '@material-ui/core';
import APIError from 'components/AlertScreens/APIError';
import EmptyError from 'components/AlertScreens/EmptyError';
import FormSkeletonLoader from 'components/AlertScreens/FormSkeletonLoader';
import useAsync from 'components/hooks/useAsync';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useDispatch } from 'react-redux';
import SidepanelFooter from 'screens/admin/common/sidepanelFooter';
import SidepanelHeader from 'screens/admin/common/sidepanelHeader';
import styled from 'styled-components';
import { Divider1, StyledBox, StyledGrid, StyledPaper, StyledTextField } from 'styles/common';

import { generateKey, getColorFromKey } from 'constants/helper';

import { showErrorSnackbar } from 'services/snackbar';
import { createTemplateStructure, getTemplateStructure, updateTemplateStructure } from 'services/templates/review';

import { Typography10 } from 'uiToolkit/Typography';

import { useReviewTemplate } from '../common/ReviewTemplateContext';
import { BodyContainer, FullHeightBox, TemplateTextField } from '../common/styles';
import { useTemplateCreationSidePanel } from './TemplateCreationSidepanelContext';

const SECTION_TYPES = {
  PERFORMANCE: 'PERFORMANCE',
  POTENTIAL: 'POTENTIAL',
  QNA: 'QNA',
};

const TEMPLATE_STRUCTURE = [
  {
    id: SECTION_TYPES.PERFORMANCE,
    name: 'Goals',
  },
  {
    id: SECTION_TYPES.POTENTIAL,
    name: 'Competency',
  },
  {
    id: SECTION_TYPES.QNA,
    name: 'Overall questions',
  },
];

const MAX_TOTAL_WEIGHTAGE = 100;

const TemplateCreationSidepanel = () => {
  const [totalWeightage, setTotalWeightage] = useState(0);
  const [loading, setLoading] = useState(null);
  const [showHelloBar, setShowHelloBar] = useState(true);

  const { data, run, isLoading, isError, reset } = useAsync();
  const dispatch = useDispatch();
  const {
    state: { open, onClose, onSuccess, templateId, isSample },
    hideSidepanel,
  } = useTemplateCreationSidePanel();
  const {
    state: { META },
    setMeta,
    clearMeta,
  } = useReviewTemplate();

  const handleClose = () => {
    hideSidepanel();
    clearMeta();
    reset();
    onClose && onClose();
    setShowHelloBar(true);
  };
  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const handlePrimaryAction = () => {
    // if used in ongoing review cycle, open preview and close sidebar
    if (isOngoingReviewCycle) {
      hideSidepanel();
      onSuccess && onSuccess({ templateId, goToPreview: true });
      return;
    }

    // if sample template, open preview and close sidebar
    if (isSample) {
      hideSidepanel();
      onSuccess && onSuccess({ templateId });
      return;
    }

    const primaryAction = templateId ? updateTemplate : saveTemplate;
    setLoading(true);
    const overallRatingDistribution = {};
    Object.keys(META.templateSetting.overallRatingDistribution).forEach((key) => {
      if (
        META.templateSetting.overallRatingDistribution[key] !== null &&
        META.templateSetting.overallRatingDistribution[key] >= 0
      ) {
        overallRatingDistribution[key] = META.templateSetting.overallRatingDistribution[key];
      }
    });
    const body = {
      ...META,
      templateSetting: {
        ...META.templateSetting,
        overallRatingDistribution: { ...overallRatingDistribution },
      },
    };
    primaryAction({ templateId, body })
      .then((res) => {
        hideSidepanel();
        onSuccess && onSuccess({ templateId: templateId || res });
      })
      .catch((err) => {
        dispatch(showErrorSnackbar(err.message || 'Something went wrong!'));
      })
      .finally(() => setLoading(false));
  };

  const getData = () => {
    clearMeta();
    run(getTemplateStructure({ templateId }));
  };

  const saveTemplate = ({ body }) => createTemplateStructure({ body });

  const updateTemplate = ({ templateId, body }) => updateTemplateStructure({ templateId, body });

  useEffect(() => {
    if (templateId) {
      getData();
    }
  }, [templateId]);

  useEffect(() => {
    data && setMeta({ meta: data });
  }, [data]);

  const {
    name,
    templateSetting: { overallRatingDistribution },
    ongoingReviewCycleCount,
  } = META;

  const sumTotalWeightage = () => {
    let totalWeight = 0;
    if (overallRatingDistribution) {
      Object.values(overallRatingDistribution).forEach((weight) => {
        totalWeight += Number(weight) || 0;
      });
    }
    setTotalWeightage(totalWeight);
  };

  useEffect(() => {
    if (overallRatingDistribution) {
      sumTotalWeightage();
    }
  }, [overallRatingDistribution]);

  const totalWeightageFixed = totalWeightage.toFixed(2);
  const maxWeightageFixed = MAX_TOTAL_WEIGHTAGE.toFixed(2);

  const handleUpdate = (param, value) => {
    const isSection = Boolean(SECTION_TYPES[param]);
    if (isSection) {
      handleWeightChange(param, value);
    } else {
      const newMeta = { ...META, [param]: value };
      setMeta({ meta: newMeta });
    }
  };

  const handleWeightChange = (param, value) => {
    if (value < 0 || value > 100 || Number.isNaN(value)) {
      dispatch(showErrorSnackbar('Valid Input Range: [0,100]'));
    } else {
      const newMeta = {
        ...META,
        templateSetting: {
          ...META.templateSetting,
          overallRatingDistribution: { ...overallRatingDistribution, [param]: value },
        },
      };
      setMeta({ meta: newMeta });
    }
  };

  const handleCheckSection = (section, checked) => {
    if (checked) {
      handleWeightChange(section, 0);
    } else {
      handleWeightChange(section, null);
    }
  };

  const headingText = isSample ? 'Sample Template' : 'Add New Template';

  const isOngoingReviewCycle = ongoingReviewCycleCount > 0;

  const primaryActionText = isSample || isOngoingReviewCycle ? 'Preview' : templateId ? 'Modify' : 'Create';

  let tooltipText = '';

  if (isOngoingReviewCycle) {
    tooltipText = 'This template is in use in active review cycles. Hence it can only be previewed and not modified';
  } else if (isSample) {
    tooltipText = 'This is a sample template. You can preview it but not modify it';
  }

  return (
    <StyledDrawer anchor="right" open={open} onClose={handleClose} className="mesh-app-drawer">
      <SidepanelHeader text={headingText} onCancel={handleClose} />
      <BodyContainer>
        {isError ? (
          <APIError reloadHandler={getData} />
        ) : isLoading ? (
          <FormSkeletonLoader />
        ) : !templateId || data ? (
          <>
            <TemplateTextField
              variant="outlined"
              placeholder="Type template name here *"
              fullWidth
              value={name}
              onChange={(e) => handleUpdate('name', e.target.value)}
              disabled={isSample || isOngoingReviewCycle}
            />
            <Divider1 topGutter={4} bottomGutter={3} />
            <Typography10 variant="body1" fontsize="16px">
              Template Structure
            </Typography10>
            <Typography10 color="textSecondary" fontsize="12px">
              Please select sections that will appear in you review template
            </Typography10>

            <StyledGrid topGutter={3} container alignItems="center" justify="center">
              {TEMPLATE_STRUCTURE.map((temp) => (
                <StyledGrid topGutter={1} bottomGutter={1} key={temp.id} item xs={12}>
                  <StyledPaper padding="0.75rem 0rem" variant="outlined" flexcontainer justifycontent="space-between">
                    <StyledBox flex="2" flexcontainer justifycontent="flex-start" alignitems="center">
                      <Checkbox
                        color="primary"
                        disabled={isSample || isOngoingReviewCycle}
                        id={generateKey(8)}
                        onChange={(e) => handleCheckSection(temp.id, e.target.checked)}
                        checked={
                          overallRatingDistribution[temp.id] !== null && Number(overallRatingDistribution[temp.id] >= 0)
                        }
                      />
                      <Typography10 style={{ fontSize: '12px' }}>
                        {temp.name === 'Goals' ? moduleName : temp.name}
                      </Typography10>
                    </StyledBox>
                    <StyledBox flex="1" padding="0 1rem" flexcontainer justifycontent="center" alignitems="center">
                      <StyledBox flex="1">
                        <Typography10 fontsize="12px" color="textSecondary" variant="body2">
                          Weightage
                        </Typography10>
                      </StyledBox>
                      <StyledBox flex="1">
                        <StyledTextField
                          variant="outlined"
                          size="small"
                          disabled={isSample || isOngoingReviewCycle}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                          value={
                            overallRatingDistribution[temp.id] >= 0 && overallRatingDistribution[temp.id] !== null
                              ? overallRatingDistribution[temp.id]
                              : ''
                          }
                          onChange={(e) => handleUpdate(temp.id, e.target.value)}
                        />
                      </StyledBox>
                    </StyledBox>
                  </StyledPaper>
                </StyledGrid>
              ))}
            </StyledGrid>
            <FooterBox>
              <StyledGrid container justify="space-between" align="center">
                <StyledGrid item xs={3}>
                  <FullHeightBox flexcontainer justifycontent="center" alignitems="center">
                    <Typography10 fontsize="14px" variant="body1" weight={600} color="textSecondary">
                      Total
                    </Typography10>
                  </FullHeightBox>
                </StyledGrid>
                <StyledGrid item xs={3}>
                  <Typography10
                    variant="body1"
                    weight={600}
                    fontsize="24px"
                    color={getColorFromKey(totalWeightageFixed === maxWeightageFixed ? 'green' : 'red')}
                  >
                    {totalWeightageFixed}%
                  </Typography10>
                </StyledGrid>
              </StyledGrid>
            </FooterBox>
          </>
        ) : (
          <EmptyError />
        )}
      </BodyContainer>
      <SidepanelFooter
        onCancel={handleClose}
        onSave={handlePrimaryAction}
        primaryActionText={primaryActionText}
        disabled={!META.name || totalWeightageFixed !== maxWeightageFixed}
        loading={loading || isLoading}
        buttonHeight="2.5rem"
        showTooltip={isOngoingReviewCycle}
        tooltip={tooltipText}
      />
    </StyledDrawer>
  );
};

export default TemplateCreationSidepanel;

const StyledDrawer = styled(Drawer)`
  ${(props) => props.theme.breakpoints.up('sm')} {
    .MuiDrawer-paper {
      width: 40%;
      overflow: hidden;
    }
  }
`;

const FooterBox = styled.div`
  position: absolute;
  bottom: 8vh;
  right: 0;
  width: 100%;
  height: 8vh;
  background: ${(props) => getColorFromKey(props.theme.palette.background.lightGrey4)};
  display: flex;
  align-items: center;
`;
